import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, Shield, Plus, Trash2, X, Check, Key, Search,
  AlertCircle, Lock, Camera, Pencil, UserX, UserCheck,
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Usuarios y Roles (RBAC, migración 0009).
 *
 *  Tab 1: Usuarios — alta/baja, roles asignados y alcance por equipo.
 *  Tab 2: Roles    — matriz de permisos por módulo.
 *
 * Todo lo que se esconde aquí lo vuelve a comprobar el backend: esta pantalla
 * es para que el sistema sea administrable, no es la barrera de seguridad.
 */
const Usuarios = () => {
  const { user: yo, refresh } = useAuth();
  const [tab, setTab] = useState('usuarios');

  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editUser, setEditUser] = useState(null);   // objeto usuario o 'nuevo'
  const [editRole, setEditRole] = useState(null);   // objeto rol o 'nuevo'
  const [passUser, setPassUser] = useState(null);
  const [buscar, setBuscar] = useState('');

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const [u, r, p, d] = await Promise.all([
        api.get('/api/users'),
        api.get('/api/roles'),
        api.get('/api/permissions'),
        api.get('/api/devices'),
      ]);
      setUsuarios(u.data || []);
      setRoles(r.data || []);
      setModulos(p.data?.modules || []);
      setEquipos(d.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const puedeAdministrar = useMemo(
    () => yo?.rbac_enabled === false || (yo?.permissions || []).includes('users.manage'),
    [yo]
  );

  const visibles = usuarios.filter(u =>
    !buscar ||
    u.username.toLowerCase().includes(buscar.toLowerCase()) ||
    (u.full_name || '').toLowerCase().includes(buscar.toLowerCase())
  );

  // Tras tocar mi propia cuenta hay que releer /api/auth/me: si no, el menú
  // sigue mostrando lo que ya no puedo hacer hasta que recargue la página.
  const trasGuardar = async (idTocado) => {
    await cargar();
    if (idTocado === yo?.id) await refresh();
  };

  const desactivar = async (u) => {
    if (!window.confirm(
      `¿Desactivar a "${u.username}"?\n\nNo se borra: conserva su historial y ` +
      `puedes reactivarlo después. Dejará de poder entrar de inmediato.`
    )) return;
    try {
      await api.delete(`/api/users/${u.id}`);
      cargar();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const reactivar = async (u) => {
    try {
      await api.put(`/api/users/${u.id}`, { is_active: true });
      cargar();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><Users size={26} /> Usuarios y Roles</h1>
          <p style={S.subtitle}>Quién entra al panel y qué puede hacer dentro</p>
        </div>
        {puedeAdministrar && (
          <div style={{ display: 'flex', gap: 8 }}>
            {tab === 'usuarios'
              ? <button style={S.btnPrimary} onClick={() => setEditUser('nuevo')}>
                  <Plus size={16} /> Nuevo usuario
                </button>
              : <button style={S.btnPrimary} onClick={() => setEditRole('nuevo')}>
                  <Plus size={16} /> Nuevo rol
                </button>}
          </div>
        )}
      </header>

      {error && <div style={S.errorBox}><AlertCircle size={18} /> {error}</div>}

      {!puedeAdministrar && !error && (
        <div style={S.infoBox}>
          <Lock size={16} /> Tienes acceso de solo lectura a esta sección.
        </div>
      )}

      <div style={S.tabs}>
        <Tab activo={tab === 'usuarios'} onClick={() => setTab('usuarios')}
             Icon={Users} label={`Usuarios (${usuarios.length})`} />
        <Tab activo={tab === 'roles'} onClick={() => setTab('roles')}
             Icon={Shield} label={`Roles (${roles.length})`} />
      </div>

      {loading ? <div style={S.loading}>Cargando…</div> : tab === 'usuarios' ? (
        <>
          <div style={S.searchRow}>
            <Search size={16} color="#94a3b8" />
            <input style={S.search} placeholder="Buscar por usuario o nombre…"
                   value={buscar} onChange={e => setBuscar(e.target.value)} />
          </div>
          <div style={S.card}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Usuario</th>
                  <th style={S.th}>Roles</th>
                  <th style={S.th}>Equipos</th>
                  <th style={S.th}>Último acceso</th>
                  <th style={S.th}>Estado</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map(u => (
                  <tr key={u.id} style={{ ...S.tr, opacity: u.is_active ? 1 : 0.5 }}>
                    <td style={S.td}>
                      <div style={S.userCell}>
                        <div style={S.avatar}>{u.username[0]?.toUpperCase()}</div>
                        <div>
                          <div style={S.userName}>
                            {u.full_name || u.username}
                            {u.id === yo?.id && <span style={S.chipYo}>tú</span>}
                          </div>
                          <div style={S.userSub}>{u.username}{u.email ? ` · ${u.email}` : ''}</div>
                        </div>
                      </div>
                    </td>
                    <td style={S.td}>
                      {u.roles.length === 0
                        ? <span style={S.sinRol}>Sin rol · no puede hacer nada</span>
                        : u.roles.map(r => <span key={r.id} style={S.chipRol}>{r.name}</span>)}
                    </td>
                    <td style={S.td}>
                      {u.all_devices
                        ? <span style={S.chipFlota}>Toda la flota</span>
                        : <span style={S.chipLimitado} title={u.devices.join(', ')}>
                            <Camera size={12} /> {u.devices.length}
                          </span>}
                    </td>
                    <td style={{ ...S.td, color: '#64748b', fontSize: 12.5 }}>
                      {fecha(u.last_login) || 'Nunca'}
                    </td>
                    <td style={S.td}>
                      {u.is_active
                        ? <span style={S.badgeOk}>Activo</span>
                        : <span style={S.badgeOff}>Desactivado</span>}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {puedeAdministrar && (
                        <>
                          <IconBtn title="Editar" onClick={() => setEditUser(u)}><Pencil size={15} /></IconBtn>
                          <IconBtn title="Cambiar contraseña" onClick={() => setPassUser(u)}><Key size={15} /></IconBtn>
                          {u.is_active
                            ? <IconBtn title="Desactivar" danger onClick={() => desactivar(u)}><UserX size={15} /></IconBtn>
                            : <IconBtn title="Reactivar" onClick={() => reactivar(u)}><UserCheck size={15} /></IconBtn>}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {visibles.length === 0 && (
                  <tr><td style={{ ...S.td, textAlign: 'center', color: '#94a3b8' }} colSpan={6}>
                    Sin resultados
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div style={S.rolesGrid}>
          {roles.map(r => (
            <div key={r.id} style={S.roleCard}>
              <div style={S.roleHead}>
                <div>
                  <div style={S.roleName}>
                    <Shield size={16} /> {r.name}
                    {r.is_system && <span style={S.chipSys}>sistema</span>}
                  </div>
                  <div style={S.roleDesc}>{r.description || 'Sin descripción'}</div>
                </div>
                {puedeAdministrar && (
                  <div style={{ whiteSpace: 'nowrap' }}>
                    <IconBtn title="Editar permisos" onClick={() => setEditRole(r)}><Pencil size={15} /></IconBtn>
                    {!r.is_system && (
                      <IconBtn title="Eliminar" danger onClick={async () => {
                        if (!window.confirm(`¿Eliminar el rol "${r.name}"?`)) return;
                        try { await api.delete(`/api/roles/${r.id}`); cargar(); }
                        catch (e) { alert(e.response?.data?.detail || 'Error'); }
                      }}><Trash2 size={15} /></IconBtn>
                    )}
                  </div>
                )}
              </div>
              <div style={S.roleStats}>
                <span>{r.permissions.length} permisos</span>
                <span>·</span>
                <span>{r.user_count} usuario{r.user_count === 1 ? '' : 's'}</span>
              </div>
              <div style={S.permChips}>
                {r.permissions.slice(0, 10).map(p => <span key={p} style={S.chipPerm}>{p}</span>)}
                {r.permissions.length > 10 &&
                  <span style={S.chipPerm}>+{r.permissions.length - 10} más</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {editUser && (
        <ModalUsuario
          usuario={editUser === 'nuevo' ? null : editUser}
          roles={roles} equipos={equipos} soyYo={editUser !== 'nuevo' && editUser.id === yo?.id}
          onClose={() => setEditUser(null)}
          onSaved={async (id) => { setEditUser(null); await trasGuardar(id); }}
        />
      )}
      {editRole && (
        <ModalRol
          rol={editRole === 'nuevo' ? null : editRole}
          modulos={modulos}
          onClose={() => setEditRole(null)}
          onSaved={async () => { setEditRole(null); await cargar(); await refresh(); }}
        />
      )}
      {passUser && (
        <ModalPassword usuario={passUser} onClose={() => setPassUser(null)}
                       onSaved={() => setPassUser(null)} />
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ usuario */

const ModalUsuario = ({ usuario, roles, equipos, soyYo, onClose, onSaved }) => {
  const nuevo = !usuario;
  const [username, setUsername] = useState(usuario?.username || '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState(usuario?.full_name || '');
  const [email, setEmail] = useState(usuario?.email || '');
  const [activo, setActivo] = useState(usuario?.is_active ?? true);
  const [todaFlota, setTodaFlota] = useState(usuario?.all_devices ?? true);
  const [rolesSel, setRolesSel] = useState(usuario?.roles.map(r => r.id) || []);
  const [equiposSel, setEquiposSel] = useState(usuario?.devices || []);
  const [filtro, setFiltro] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const equiposFiltrados = equipos.filter(d =>
    !filtro ||
    (d.name || '').toLowerCase().includes(filtro.toLowerCase()) ||
    (d.device_id || '').toLowerCase().includes(filtro.toLowerCase())
  );

  const guardar = async () => {
    setError('');
    if (nuevo && password.length < 8) {
      return setError('La contraseña debe tener al menos 8 caracteres');
    }
    if (!todaFlota && equiposSel.length === 0) {
      return setError('Marcaste "solo estos equipos" pero no seleccionaste ninguno: ' +
                      'el usuario no podría ver nada');
    }
    if (rolesSel.length === 0 &&
        !window.confirm('Sin ningún rol el usuario podrá entrar pero no ver nada. ¿Continuar?')) {
      return;
    }
    setGuardando(true);
    try {
      const cuerpo = {
        full_name: fullName || null,
        email: email || null,
        is_active: activo,
        all_devices: todaFlota,
        role_ids: rolesSel,
        device_ids: todaFlota ? [] : equiposSel,
      };
      if (nuevo) {
        const r = await api.post('/api/users', { ...cuerpo, username, password });
        onSaved(r.data?.id);
      } else {
        await api.put(`/api/users/${usuario.id}`, cuerpo);
        onSaved(usuario.id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal titulo={nuevo ? 'Nuevo usuario' : `Editar: ${usuario.username}`} onClose={onClose}>
      {error && <div style={S.errorBox}><AlertCircle size={16} /> {error}</div>}

      <div style={S.formGrid}>
        <Campo label="Usuario">
          <input style={S.input} value={username} disabled={!nuevo}
                 onChange={e => setUsername(e.target.value)}
                 placeholder="jperez" />
        </Campo>
        <Campo label="Nombre completo">
          <input style={S.input} value={fullName}
                 onChange={e => setFullName(e.target.value)}
                 placeholder="Juan Pérez" />
        </Campo>
        {nuevo && (
          <Campo label="Contraseña (mínimo 8)">
            <input style={S.input} type="password" value={password}
                   onChange={e => setPassword(e.target.value)} />
          </Campo>
        )}
        <Campo label="Correo">
          <input style={S.input} value={email} onChange={e => setEmail(e.target.value)}
                 placeholder="opcional" />
        </Campo>
      </div>

      {!nuevo && (
        <label style={{ ...S.check, marginTop: 4 }}>
          <input type="checkbox" checked={activo} disabled={soyYo}
                 onChange={e => setActivo(e.target.checked)} />
          <span>Cuenta activa {soyYo && <em style={S.nota}>— no puedes desactivarte a ti mismo</em>}</span>
        </label>
      )}

      <Seccion titulo="Roles" nota="Los permisos se suman: si un rol lo concede, el usuario lo tiene.">
        <div style={S.rolesPick}>
          {roles.map(r => (
            <label key={r.id} style={{
              ...S.rolePick,
              ...(rolesSel.includes(r.id) ? S.rolePickOn : {}),
            }}>
              <input type="checkbox" checked={rolesSel.includes(r.id)}
                     onChange={e => setRolesSel(
                       e.target.checked ? [...rolesSel, r.id] : rolesSel.filter(x => x !== r.id)
                     )} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                <div style={{ fontSize: 11.5, color: '#64748b' }}>
                  {r.description || `${r.permissions.length} permisos`}
                </div>
              </div>
            </label>
          ))}
        </div>
      </Seccion>

      <Seccion titulo="Alcance"
               nota="Qué equipos puede ver. Aplica a vivo, grabaciones, salud, alertas y consumo.">
        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
          <label style={S.check}>
            <input type="radio" checked={todaFlota} onChange={() => setTodaFlota(true)} />
            <span>Toda la flota</span>
          </label>
          <label style={S.check}>
            <input type="radio" checked={!todaFlota} onChange={() => setTodaFlota(false)} />
            <span>Solo estos equipos</span>
          </label>
        </div>

        {!todaFlota && (
          <>
            <div style={{ ...S.searchRow, marginBottom: 8 }}>
              <Search size={15} color="#94a3b8" />
              <input style={S.search} placeholder="Filtrar equipos…"
                     value={filtro} onChange={e => setFiltro(e.target.value)} />
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {equiposSel.length} de {equipos.length}
              </span>
            </div>
            <div style={S.devicesBox}>
              {equiposFiltrados.map(d => (
                <label key={d.device_id} style={S.deviceRow}>
                  <input type="checkbox" checked={equiposSel.includes(d.device_id)}
                         onChange={e => setEquiposSel(
                           e.target.checked
                             ? [...equiposSel, d.device_id]
                             : equiposSel.filter(x => x !== d.device_id)
                         )} />
                  <span style={{ fontWeight: 500 }}>{d.name}</span>
                  <span style={{ color: '#94a3b8', fontSize: 11.5 }}>{d.device_id}</span>
                </label>
              ))}
              {equiposFiltrados.length === 0 &&
                <div style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>Sin resultados</div>}
            </div>
          </>
        )}
      </Seccion>

      <div style={S.modalFooter}>
        <button style={S.btnGhost} onClick={onClose}>Cancelar</button>
        <button style={S.btnPrimary} onClick={guardar} disabled={guardando}>
          <Check size={16} /> {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </Modal>
  );
};

/* ---------------------------------------------------------------------- rol */

const ModalRol = ({ rol, modulos, onClose, onSaved }) => {
  const nuevo = !rol;
  const [name, setName] = useState(rol?.name || '');
  const [description, setDescription] = useState(rol?.description || '');
  const [sel, setSel] = useState(rol?.permissions || []);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const toggle = (code) =>
    setSel(sel.includes(code) ? sel.filter(c => c !== code) : [...sel, code]);

  const toggleModulo = (permisos) => {
    const codes = permisos.map(p => p.code);
    const todos = codes.every(c => sel.includes(c));
    setSel(todos ? sel.filter(c => !codes.includes(c))
                 : [...new Set([...sel, ...codes])]);
  };

  const guardar = async () => {
    setError('');
    if (name.trim().length < 2) return setError('El nombre es muy corto');
    setGuardando(true);
    try {
      if (nuevo) await api.post('/api/roles', { name, description, permissions: sel });
      else await api.put(`/api/roles/${rol.id}`, { name, description, permissions: sel });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal titulo={nuevo ? 'Nuevo rol' : `Editar rol: ${rol.name}`} onClose={onClose}>
      {error && <div style={S.errorBox}><AlertCircle size={16} /> {error}</div>}

      <div style={S.formGrid}>
        <Campo label="Nombre">
          <input style={S.input} value={name} disabled={rol?.is_system}
                 onChange={e => setName(e.target.value)} placeholder="monitorista" />
        </Campo>
        <Campo label="Descripción">
          <input style={S.input} value={description}
                 onChange={e => setDescription(e.target.value)}
                 placeholder="Qué hace este rol" />
        </Campo>
      </div>
      {rol?.is_system && (
        <div style={S.infoBox}>
          <Lock size={15} /> Rol del sistema: puedes ajustar sus permisos, pero no renombrarlo
          ni eliminarlo.
        </div>
      )}

      <Seccion titulo={`Permisos (${sel.length})`}
               nota="Marca lo que este rol puede hacer. El backend valida cada llamada.">
        {modulos.map(m => {
          const codes = m.permissions.map(p => p.code);
          const todos = codes.every(c => sel.includes(c));
          return (
            <div key={m.module} style={S.moduloBox}>
              <div style={S.moduloHead}>
                <span style={S.moduloName}>{m.module}</span>
                <button style={S.btnMini} onClick={() => toggleModulo(m.permissions)}>
                  {todos ? 'Quitar todo' : 'Marcar todo'}
                </button>
              </div>
              <div style={S.permGrid}>
                {m.permissions.map(p => (
                  <label key={p.code} style={{
                    ...S.permPick,
                    ...(sel.includes(p.code) ? S.permPickOn : {}),
                  }}>
                    <input type="checkbox" checked={sel.includes(p.code)}
                           onChange={() => toggle(p.code)} />
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 500 }}>{p.label}</div>
                      <code style={S.permCode}>{p.code}</code>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </Seccion>

      <div style={S.modalFooter}>
        <button style={S.btnGhost} onClick={onClose}>Cancelar</button>
        <button style={S.btnPrimary} onClick={guardar} disabled={guardando}>
          <Check size={16} /> {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </Modal>
  );
};

/* --------------------------------------------------------------- contraseña */

const ModalPassword = ({ usuario, onClose, onSaved }) => {
  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setError('');
    if (pass.length < 8) return setError('Mínimo 8 caracteres');
    if (pass !== pass2) return setError('Las contraseñas no coinciden');
    setGuardando(true);
    try {
      await api.post(`/api/users/${usuario.id}/password`, { new_password: pass });
      alert(`Contraseña de "${usuario.username}" actualizada`);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cambiar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal titulo={`Contraseña de ${usuario.username}`} onClose={onClose} ancho={420}>
      {error && <div style={S.errorBox}><AlertCircle size={16} /> {error}</div>}
      <Campo label="Nueva contraseña">
        <input style={S.input} type="password" value={pass} onChange={e => setPass(e.target.value)} />
      </Campo>
      <Campo label="Repetir">
        <input style={S.input} type="password" value={pass2} onChange={e => setPass2(e.target.value)} />
      </Campo>
      <div style={S.modalFooter}>
        <button style={S.btnGhost} onClick={onClose}>Cancelar</button>
        <button style={S.btnPrimary} onClick={guardar} disabled={guardando}>
          <Key size={16} /> {guardando ? 'Guardando…' : 'Cambiar'}
        </button>
      </div>
    </Modal>
  );
};

/* ------------------------------------------------------------------- piezas */

const Modal = ({ titulo, children, onClose, ancho = 760 }) => (
  <div style={S.overlay} onClick={onClose}>
    <div style={{ ...S.modal, maxWidth: ancho }} onClick={e => e.stopPropagation()}>
      <div style={S.modalHead}>
        <h2 style={S.modalTitle}>{titulo}</h2>
        <button style={S.closeBtn} onClick={onClose}><X size={18} /></button>
      </div>
      <div style={S.modalBody}>{children}</div>
    </div>
  </div>
);

const Campo = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <label style={S.label}>{label}</label>
    {children}
  </div>
);

const Seccion = ({ titulo, nota, children }) => (
  <div style={{ marginTop: 18 }}>
    <div style={S.seccionTitulo}>{titulo}</div>
    {nota && <div style={S.seccionNota}>{nota}</div>}
    {children}
  </div>
);

const Tab = ({ activo, onClick, Icon, label }) => (
  <button onClick={onClick} style={{ ...S.tab, ...(activo ? S.tabOn : {}) }}>
    <Icon size={15} /> {label}
  </button>
);

const IconBtn = ({ children, title, onClick, danger }) => (
  <button title={title} onClick={onClick}
          style={{ ...S.iconBtn, ...(danger ? S.iconBtnDanger : {}) }}>
    {children}
  </button>
);

const fecha = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d.toLocaleString('es-MX', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

/* ------------------------------------------------------------------ estilos */

const S = {
  container: { padding: '26px 30px', maxWidth: 1280 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, gap: 16 },
  title: { fontSize: 24, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 10, margin: 0 },
  subtitle: { color: '#64748b', fontSize: 13.5, margin: '6px 0 0' },

  tabs: { display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid #e2e8f0' },
  tab: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', border: 'none', background: 'none', color: '#64748b', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', borderBottom: '2px solid transparent' },
  tabOn: { color: '#2563eb', borderBottom: '2px solid #2563eb' },

  searchRow: { display: 'flex', alignItems: 'center', gap: 8, background: 'white', border: '1px solid #e2e8f0', borderRadius: 9, padding: '9px 12px', marginBottom: 12 },
  search: { flex: 1, border: 'none', outline: 'none', fontSize: 13.5, background: 'transparent' },

  card: { background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '11px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .6, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '11px 14px', fontSize: 13.5, color: '#1e293b', verticalAlign: 'middle' },

  userCell: { display: 'flex', alignItems: 'center', gap: 10 },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  userName: { fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 },
  userSub: { fontSize: 11.5, color: '#94a3b8' },

  chipYo: { fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', padding: '1px 6px', borderRadius: 5 },
  chipRol: { display: 'inline-block', fontSize: 11.5, fontWeight: 600, background: '#eef2ff', color: '#4338ca', padding: '3px 8px', borderRadius: 6, marginRight: 4 },
  chipSys: { fontSize: 10, fontWeight: 700, background: '#f1f5f9', color: '#64748b', padding: '1px 6px', borderRadius: 5, marginLeft: 6 },
  chipFlota: { fontSize: 11.5, fontWeight: 600, color: '#059669', background: '#ecfdf5', padding: '3px 8px', borderRadius: 6 },
  chipLimitado: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: '#b45309', background: '#fffbeb', padding: '3px 8px', borderRadius: 6, cursor: 'help' },
  chipPerm: { fontSize: 10.5, fontFamily: 'ui-monospace, monospace', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', padding: '2px 6px', borderRadius: 5 },
  sinRol: { fontSize: 12, color: '#dc2626', fontStyle: 'italic' },

  badgeOk: { fontSize: 11.5, fontWeight: 600, color: '#059669', background: '#ecfdf5', padding: '3px 9px', borderRadius: 20 },
  badgeOff: { fontSize: 11.5, fontWeight: 600, color: '#dc2626', background: '#fef2f2', padding: '3px 9px', borderRadius: 20 },

  rolesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 },
  roleCard: { background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 },
  roleHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 },
  roleName: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 15, fontWeight: 700, color: '#0f172a' },
  roleDesc: { fontSize: 12.5, color: '#64748b', marginTop: 3 },
  roleStats: { display: 'flex', gap: 7, fontSize: 12, color: '#94a3b8', margin: '10px 0 8px' },
  permChips: { display: 'flex', flexWrap: 'wrap', gap: 4 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50 },
  modal: { background: 'white', borderRadius: 14, width: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,.35)' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' },
  modalTitle: { fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 },
  modalBody: { padding: '18px 20px', overflowY: 'auto' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 22, paddingTop: 14, borderTop: '1px solid #e2e8f0' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4, display: 'flex' },

  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  label: { fontSize: 12, fontWeight: 600, color: '#475569' },
  input: { width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, outline: 'none', boxSizing: 'border-box' },
  check: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: '#334155', cursor: 'pointer' },
  nota: { color: '#94a3b8', fontSize: 12 },

  seccionTitulo: { fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 3 },
  seccionNota: { fontSize: 12, color: '#64748b', marginBottom: 10 },

  rolesPick: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 8 },
  rolePick: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: 9, cursor: 'pointer', background: 'white' },
  rolePickOn: { borderColor: '#3b82f6', background: '#eff6ff' },

  devicesBox: { maxHeight: 220, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 9 },
  deviceRow: { display: 'flex', alignItems: 'center', gap: 9, padding: '7px 11px', fontSize: 13, borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },

  moduloBox: { border: '1px solid #e2e8f0', borderRadius: 9, marginBottom: 10, overflow: 'hidden' },
  moduloHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  moduloName: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .6, color: '#475569' },
  permGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px,1fr))', gap: 6, padding: 10 },
  permPick: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 9px', border: '1px solid transparent', borderRadius: 7, cursor: 'pointer' },
  permPickOn: { borderColor: '#bfdbfe', background: '#eff6ff' },
  permCode: { fontSize: 10.5, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' },

  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: 7, background: '#2563eb', color: 'white', border: 'none', padding: '9px 15px', borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'white', color: '#475569', border: '1px solid #e2e8f0', padding: '9px 15px', borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' },
  btnMini: { background: 'white', color: '#2563eb', border: '1px solid #e2e8f0', padding: '3px 9px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' },
  iconBtn: { background: 'white', border: '1px solid #e2e8f0', color: '#475569', padding: 7, borderRadius: 7, cursor: 'pointer', marginLeft: 5, display: 'inline-flex' },
  iconBtnDanger: { color: '#dc2626', borderColor: '#fecaca' },

  errorBox: { display: 'flex', alignItems: 'center', gap: 8, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', padding: '10px 13px', borderRadius: 9, fontSize: 13, marginBottom: 12 },
  infoBox: { display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', padding: '10px 13px', borderRadius: 9, fontSize: 13, marginBottom: 12 },
  loading: { padding: 40, textAlign: 'center', color: '#94a3b8' },
};

export default Usuarios;
